import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@shared/api/client";

const BASE = "/settings/rrhh";

export const rrhhConfigKeys = {
  all: ["settings", "rrhh"] as const,
  politicas: () => [...rrhhConfigKeys.all, "politicas"] as const,
  departamentos: (emisorId?: number | null) => [...rrhhConfigKeys.all, "departamentos", emisorId ?? "all"] as const,
  festivos: (anio: number) => [...rrhhConfigKeys.all, "festivos", anio] as const,
};

export interface RrhhPoliticas {
  rrhh_vacaciones_dias_anuales: string;
  rrhh_vacaciones_carry_max: string;
  rrhh_vacaciones_carry_limite: string;
  rrhh_vacaciones_aprobacion: string;
  rrhh_jornada_inicio: string;
  rrhh_jornada_fin: string;
  rrhh_fichaje_tolerancia: string;
}
export interface Departamento {
  id: number;
  emisor_id: number | null;
  nombre: string;
  activo: boolean;
  orden: number;
  emisor?: { id: number; nombre: string } | null;
}
export interface Festivo {
  id: number;
  fecha: string;
  nombre: string;
  ambito: string;
}

// ── Políticas ────────────────────────────────────────────────────────────────
export function usePoliticasRrhh() {
  return useQuery({
    queryKey: rrhhConfigKeys.politicas(),
    queryFn: async () => {
      const { data } = await api.get<{ data: RrhhPoliticas }>(`${BASE}/politicas`);
      return data.data;
    },
  });
}
export function useUpdatePoliticasRrhh() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<Record<keyof RrhhPoliticas, string | number>>) => {
      const { data } = await api.patch(`${BASE}/politicas`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.politicas() }),
  });
}

// ── Departamentos ──────────────────────────────────────────────────────────
export function useDepartamentos(emisorId?: number | null) {
  return useQuery({
    queryKey: rrhhConfigKeys.departamentos(emisorId),
    queryFn: async () => {
      const { data } = await api.get<{ data: Departamento[] }>(`${BASE}/departamentos`, {
        params: { emisor_id: emisorId ?? undefined },
      });
      return data.data;
    },
  });
}
export function useCreateDepartamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { emisor_id: number; nombre: string; activo?: boolean }) => {
      const { data } = await api.post(`${BASE}/departamentos`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.all }),
  });
}
export function useUpdateDepartamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: number;
      payload: Partial<{ nombre: string; activo: boolean; orden: number }>;
    }) => {
      const { data } = await api.patch(`${BASE}/departamentos/${id}`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.all }),
  });
}
export function useDeleteDepartamento() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/departamentos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.all }),
  });
}

// ── Festivos ─────────────────────────────────────────────────────────────────
export function useFestivos(anio: number) {
  return useQuery({
    queryKey: rrhhConfigKeys.festivos(anio),
    queryFn: async () => {
      const { data } = await api.get<{ data: Festivo[] }>(`${BASE}/festivos?anio=${anio}`);
      return data.data;
    },
  });
}
export function useCreateFestivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { fecha: string; nombre: string; ambito?: string }) => {
      const { data } = await api.post(`${BASE}/festivos`, payload);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.all }),
  });
}
export function useDeleteFestivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => api.delete(`${BASE}/festivos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: rrhhConfigKeys.all }),
  });
}

